import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { makeRequest } from "../../utils/request";
import { Dialog, InputField, RestList, Text, View } from "../../components/base";
import { IconButton } from "../../components/base/IconButton";
import { RestListButtonProps } from "../../components/base/RestList";
import { User } from "../../redux/reducers/userSlice";

type AddFundsDialogState = {
  organizationId: string;
  organizationName: string;
} | null;

let openAddFundsDialog: (state: AddFundsDialogState) => void = () => {};

const AddFundsButton = ({ id, formData }: RestListButtonProps<Record<string, string>>) => {
  return (
    <IconButton
      icon="payments"
      variant="ghost"
      title="Add Funds"
      onClick={(e) => {
        e.stopPropagation();
        openAddFundsDialog({ organizationId: id, organizationName: formData.name || id });
      }}
    />
  );
};

const AddFundsDialog = ({ state, onClose }: { state: AddFundsDialogState; onClose: () => void }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setAmount("");
    setDescription("");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    const dollars = parseFloat(amount);
    if (isNaN(dollars) || dollars <= 0) {
      setError("Please enter a valid dollar amount.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await makeRequest("/v1/credits/adjust", {
        method: "POST",
        body: {
          organizationId: state!.organizationId,
          type: "credit",
          amount: Math.round(dollars * 1_000_000),
          description: description || undefined,
        },
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add funds.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={!!state}
      onClose={handleClose}
      title={`Add Funds — ${state?.organizationName ?? ""}`}
      size="sm"
      onSave={handleSave}
      saveLabel="Add Funds"
      isSaving={isSaving}
    >
      <View className="p-4 flex flex-col gap-4">
        <InputField
          label="Amount ($)"
          placeholder="10.00"
          type="number"
          value={amount}
          onChange={setAmount}
        />
        <InputField
          label="Description (optional)"
          placeholder="e.g. Courtesy credit"
          value={description}
          onChange={setDescription}
        />
        {error && <Text className="text-sm text-red-500">{error}</Text>}
      </View>
    </Dialog>
  );
};

export const AdminOrganizationsPage = () => {
  const [searchParams] = useSearchParams();
  const isPanelOpen = searchParams.has("id");
  const [dialogState, setDialogState] = useState<AddFundsDialogState>(null);

  openAddFundsDialog = setDialogState;

  const search = async (query: string) => {
    try {
      const { data } = await makeRequest<{ data: User[] }>(`/v1/organizations/search?q=${query}`);
      return data.map((user) => user.id);
    } catch (error) {
      console.error("Error searching users:", error);
    }
    return [];
  };

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={`h-full w-full relative ${isPanelOpen ? "pr-[400px]" : ""} transition-all duration-300`}>
        <RestList
          className="h-full w-full"
          entityName="organization"
          search={search}
          fields={[
            {
              name: "id",
              label: "ID",
              readOnly: true,
            },
            {
              name: "name",
              label: "Name",
            },
            {
              name: "agentsEnabled",
              label: "Agents Enabled",
              type: "checkbox",
            },
            {
              name: "billingEnabled",
              label: "Billing Enabled",
              type: "checkbox",
            },
            {
              name: "outboundEnabled",
              label: "Outbound Enabled",
              type: "checkbox",
            },
            {
              name: "sessionCostPerMinute",
              label: "Session Cost ($/min)",
              type: "number",
            },
            {
              name: "tokenCostMultiplier",
              label: "Token Cost Multiplier",
              type: "number",
            },
            {
              name: "addFunds",
              label: "Add Funds",
              button: AddFundsButton,
            },
          ]}
        />
      </div>
      <AddFundsDialog
        state={dialogState}
        onClose={() => setDialogState(null)}
      />
    </View>
  );
};
